import { useEffect, useState } from "react";
import { Coins, ArrowRight, Crown, Sparkles } from "lucide-react";
import useNewsezeUser from "../lib/useNewsezeUser";
import RedeemModal from "./RedeemModal";
import PricingModal from "./PricingModal";

export default function CreditsWidget({ onOpenBrief }) {
  const { email, credits, refreshCredits } = useNewsezeUser();
  const [showRedeem, setShowRedeem] = useState(false);
  const [showPricing, setShowPricing] = useState(false);

  // Refresh on focus + every 30s so balance feels live
  useEffect(() => {
    const onFocus = () => refreshCredits();
    window.addEventListener("focus", onFocus);
    const id = setInterval(refreshCredits, 30000);
    return () => { window.removeEventListener("focus", onFocus); clearInterval(id); };
  }, [refreshCredits]);

  if (!email) {
    return (
      <div className="rounded-xl p-4 border border-[var(--sky-2)] bg-[var(--sky)]" data-testid="credits-widget-anon">
        <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.14em] font-black text-[var(--blue)]">
          <Coins size={14} /> Earn rewards as you read
        </div>
        <p className="text-[13px] text-[var(--ink)] mt-2 leading-snug">
          Subscribe free to earn credits for every story you read. Redeem for member benefits,
          gift cards, or donate to charity. <b>No drama. No conspiracies.</b>
        </p>
        <button onClick={onOpenBrief} className="btn primary mt-3 w-full justify-center" data-testid="credits-widget-cta">
          Start earning free <ArrowRight size={14} />
        </button>
      </div>
    );
  }

  const c = credits || {};
  const balance = c.balance ?? 0;
  const usd = c.usd_value ?? 0;
  const tier = c.tier || "free";
  const pct = Math.min(100, Math.round((balance / 5000) * 100));

  return (
    <>
      <div className="rounded-xl p-4 bg-gradient-to-br from-[#0e2d63] to-[#1e40af] text-white" data-testid="credits-widget">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-black text-[var(--gold)]">
            <Coins size={12} /> Reader Wallet
          </div>
          {tier === "pro" ? (
            <span className="flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-black text-[#1d1303] bg-[var(--gold)] px-1.5 py-0.5 rounded" data-testid="wallet-tier-pro">
              <Crown size={10} /> Pro
            </span>
          ) : tier === "lite" ? (
            <span className="text-[10px] uppercase tracking-[0.14em] font-black bg-white/20 px-1.5 py-0.5 rounded" data-testid="wallet-tier-lite">Lite</span>
          ) : (
            <span className="text-[10px] uppercase tracking-[0.14em] font-black opacity-70" data-testid="wallet-tier-free">Free</span>
          )}
        </div>
        <div className="flex items-baseline gap-2">
          <div className="text-[34px] font-black tracking-[-0.02em]">${usd.toFixed(2)}</div>
          <div className="text-[12px] opacity-80">/ ${c.cashout_threshold_usd ?? 5}</div>
        </div>
        <div className="text-[11px] opacity-80 mb-2">{balance.toLocaleString()} credits · lifetime {(c.lifetime ?? 0).toLocaleString()}</div>
        <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
          <div className="h-full bg-[var(--gold)]" style={{ width: `${pct}%` }} />
        </div>

        <button onClick={() => setShowRedeem(true)} disabled={balance < 5000} className="btn gold w-full justify-center mt-3" data-testid="credits-redeem-open">
          {balance < 5000
            ? `${(5000 - balance).toLocaleString()} more credits to redeem`
            : "Redeem credits"}
        </button>

        {tier !== "pro" && (
          <button onClick={() => setShowPricing(true)} className="w-full text-[11px] text-[var(--gold)] mt-2 flex items-center justify-center gap-1 hover:underline" data-testid="credits-upgrade-cta">
            <Sparkles size={11} /> Upgrade to unlock premium rewards
          </button>
        )}
      </div>

      {showRedeem && (
        <RedeemModal email={email} credits={c} onClose={() => setShowRedeem(false)} onDone={() => { refreshCredits(); }} />
      )}
      {showPricing && <PricingModal onClose={() => setShowPricing(false)} />}
    </>
  );
}
